import { DEFAULT_CIRCLE_RADIUS, DEFAULT_CIRCLE_STROKE_WIDTH } from "../constants/mapStyle";
import { DataType } from "../types/lib/types";
import { LAYER_GEOMETRY_TYPES } from "./smartCityStyle";
import { findExpressionConditionIndex, includesExpressionCondition, insertConditionToExpression } from "./expressionUtils";
import { uniqueFeatures } from "./propertiesUtils";


export const HIGHLIGHT_SOURCE_ID = 'search-result';
let animationFrameId: number | null = null;

// 比較用のキー
const KEY = '_id';

/**
 * lineFeatures配列をlayer.idごとにグループ化し、{ [sourceLayerId]: feature[] } の形式で返す
 */
export const groupFeaturesByLayerId = (
    lineFeatures: maplibregl.MapGeoJSONFeature[]
): { [layerId: string]: maplibregl.MapGeoJSONFeature[] } => {
    return lineFeatures.reduce((acc, feature) => {
        const layerId = feature.layer.id;
        if (!acc[layerId]) {
            acc[layerId] = [];
        }
        acc[layerId].push(feature);
        return acc;
    }, {} as { [layerId: string]: maplibregl.MapGeoJSONFeature[] });
};

/* *********************
 * 検索結果ソースを追加
 * *********************/
export const highlightFeature = (
    map: maplibregl.Map, 
    features: maplibregl.MapGeoJSONFeature[], 
    addAnimate?: boolean
): number => {
    if (!map || features.length === 0) { return 0; }

    const uniqueFeatureCount = uniqueFeatures(features).length;
    const pointFeatures = getFilteredFeatures(features, ['symbol', 'circle']);
    const lineFeatures = getFilteredFeatures(features, ['line']);
    const polygonFeatures = getFilteredFeatures(features, ['fill']);

    // circle, symbolのfeatureをハイライト
    if (pointFeatures.length) {
        const source = map.getSource(HIGHLIGHT_SOURCE_ID) as maplibregl.GeoJSONSource;
        source?.setData({
            type: 'FeatureCollection',
            features: pointFeatures
        });
    }

    // 線レイヤーをハイライト
    if (lineFeatures.length) {
        const grouped = groupFeaturesByLayerId(lineFeatures);
        for (const [sourceLayer, features] of Object.entries(grouped)) {
            const keyProperties = Array.from(new Set(features.map(f => f.properties[KEY])));
            highlightLine(map, sourceLayer, KEY, keyProperties);
        }
    }

    // 面レイヤーをハイライト
    if (polygonFeatures.length) {
        const grouped = groupFeaturesByLayerId(polygonFeatures);
        for (const [sourceLayer, features] of Object.entries(grouped)) {
            const keyProperties = Array.from(new Set(features.map(f => f.properties[KEY])));
            highlightFill(map, sourceLayer, KEY, keyProperties);
        }
    }

    if (addAnimate) { addHighlightLayer(map, HIGHLIGHT_SOURCE_ID); }

    return uniqueFeatureCount;
};


/* *********************
 * ポイントまたはシンボルのフィーチャを取得
 * *********************/
const getFilteredFeatures = (
    features: maplibregl.MapGeoJSONFeature[], 
    layerTypes: string[]
): maplibregl.MapGeoJSONFeature[] => {
    return features.filter(feature => layerTypes.includes(feature.layer.type));
}


/* *********************
 * fillレイヤーの色を更新
 * *********************/
const highlightFill = (map: maplibregl.Map, sourceLayer: string, KEY: string, keyProperties: string[]) => {
    if (!map.getLayer(sourceLayer)) { return; }
    let currentFillColor = map.getPaintProperty(sourceLayer, 'fill-color');
    let currentFillOpacity = map.getPaintProperty(sourceLayer, 'fill-opacity');
    const highlightColor = 'rgba(255, 0, 0, 1)';                        // ハイライト時の色
    const highlightOpacity = 0.7;                                         // ハイライト時のopacity

    const condition = ['in', ['get', KEY], ["literal", keyProperties]];

    // すでに条件付きでハイライトされているか確認
    const targetColor = includesExpressionCondition(currentFillColor, condition);
    const targetOpacity = includesExpressionCondition(currentFillOpacity, condition);

    // 色を条件付きで変更
    if (!targetColor) {
        currentFillColor = insertConditionToExpression(currentFillColor, condition, highlightColor);
        map.setPaintProperty(sourceLayer, 'fill-color', currentFillColor);
    }

    // opacityも条件付きで変更
    if (!targetOpacity) {
        currentFillOpacity = insertConditionToExpression(currentFillOpacity, condition, highlightOpacity);
        map.setPaintProperty(sourceLayer, 'fill-opacity', currentFillOpacity);
    }
};

/* *********************
 * fillレイヤーの色をリセット
 * *********************/
const resetFill = (map: maplibregl.Map, sourceLayer: string, KEY: string) => {
    if (!map.getLayer(sourceLayer)) { return; }
    const currentFillColor = map.getPaintProperty(sourceLayer, 'fill-color');
    const currentFillOpacity = map.getPaintProperty(sourceLayer, 'fill-opacity');
    const condition = ["in", ["get", KEY], ];

    const targetColorIndex = findExpressionConditionIndex(currentFillColor, condition, true);
    const targetOpacityIndex = findExpressionConditionIndex(currentFillOpacity, condition, true);

    // fill-colorの条件式を削除
    if (targetColorIndex !== -1 && Array.isArray(currentFillColor) && currentFillColor.length > 2) {
        currentFillColor.splice(targetColorIndex, 2);
        map.setPaintProperty(sourceLayer, 'fill-color', currentFillColor);
    }

    // fill-opacityの条件式を削除
    if (targetOpacityIndex !== -1 && Array.isArray(currentFillOpacity) && currentFillOpacity.length > 2) {
        currentFillOpacity.splice(targetOpacityIndex, 2);
        map.setPaintProperty(sourceLayer, 'fill-opacity', currentFillOpacity);
    }
};


/* *********************
 * lineレイヤーの色を更新
 * *********************/
const highlightLine = (map: maplibregl.Map, sourceLayer: string, KEY: string, keyProperties: string[]) => {

    if(!map.getLayer(sourceLayer)) { return; }

    let currentLineColor = map.getPaintProperty(sourceLayer, 'line-color');
    const highlightColor = 'rgb(255, 0, 0)';                  // ハイライト時の色

    const condition = ['in', ['get', KEY], ["literal", keyProperties]];

    // すでにハイライトされているか確認
    const targetColor = includesExpressionCondition(currentLineColor, condition);

    // ハイライトされてなかったら、色を条件付きで変更
    if (!targetColor) {
        currentLineColor = insertConditionToExpression(currentLineColor, condition, highlightColor);
        map.setPaintProperty(sourceLayer, 'line-color', currentLineColor);
    }

};

/* *********************
 * lineレイヤーの色をリセット
 * *********************/
const resetLine = (map: maplibregl.Map, sourceLayer: string, KEY: string) => {
    if(!map.getLayer(sourceLayer)) { return; }
    const currentLineColor = map.getPaintProperty(sourceLayer, 'line-color');
    const currentLineOpacity = map.getPaintProperty(sourceLayer, 'line-opacity');
    const condition = ["in", ["get", KEY], ]; // 条件式を定義

    const targetColorIndex = findExpressionConditionIndex(currentLineColor, condition, true);
    const targetOpacityIndex = findExpressionConditionIndex(currentLineOpacity, condition, true);


    // line-colorの条件式を削除
    if (targetColorIndex !== -1 && Array.isArray(currentLineColor) && currentLineColor.length > 2) {
        currentLineColor.splice(targetColorIndex, 2);
        map.setPaintProperty(sourceLayer, 'line-color', currentLineColor);
    }

    // line-opacityの条件式を削除
    if (targetOpacityIndex !== -1 && Array.isArray(currentLineOpacity) && currentLineOpacity.length > 2) {
        currentLineOpacity.splice(targetOpacityIndex, 2);
        map.setPaintProperty(sourceLayer, 'line-opacity', currentLineOpacity);
    }
};


/* *********************
 * レイヤーにアニメーションを追加
 * *********************/
export const addHighlightLayer = (mapObj: maplibregl.Map, sourceId: string) => {

    let start: null | number = null;
    const duration = 1200; // アニメーションの持続時間（ミリ秒）

    const animate = (timestamp: number) => {
        if (!start) {start = timestamp;}
        const progress = timestamp - start;
        const t = (progress % duration) / duration;
        const strokeWidth = 5 + 2 * Math.sin(t * Math.PI * 2); // アウトラインの太さを変える
        const radius = DEFAULT_CIRCLE_RADIUS + 2 * Math.cos(t * Math.PI * 2); // 円のサイズを変える（タイミングを逆に）

        mapObj.setPaintProperty(`${sourceId}/point`, 'circle-stroke-width', strokeWidth);
        mapObj.setPaintProperty(`${sourceId}/point`, 'circle-radius', radius);

        if (progress < duration) {
            animationFrameId = requestAnimationFrame(animate);
        } else {
            start = null;
            animationFrameId = requestAnimationFrame(animate);
        }
    };

    animationFrameId = requestAnimationFrame(animate);
};

/* *********************
 * レイヤーのアニメーションを削除
 * *********************/
export const removeHighlightLayer = (mapObj: maplibregl.Map, sourceId: string) => {
    if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
    }

    mapObj.setPaintProperty(`${sourceId}/point`, 'circle-stroke-width', DEFAULT_CIRCLE_STROKE_WIDTH);
    mapObj.setPaintProperty(`${sourceId}/point`, 'circle-radius', DEFAULT_CIRCLE_RADIUS);
};


export const findGyoseikaiObject = (menuItems: DataType[] | undefined) => {
    if (!menuItems) { return null; }

    const gyoseikaiObject = menuItems.find(item => item.tileId === '行政区域界');
    return gyoseikaiObject;
};


/* *********************
 * ハイライトレイヤーのソースを削除
 * *********************/
export const clearResult = (map: maplibregl.Map | null, features: maplibregl.MapGeoJSONFeature[] | undefined) => {
    if (!map || !features) { return; }

    removeHighlightLayer(map, HIGHLIGHT_SOURCE_ID);
    const resSource = map.getSource(HIGHLIGHT_SOURCE_ID);
    const linePolygonFeatures = getFilteredFeatures(features, ['line', 'fill']);

    if (resSource) {
        // circle, symbolのハイライトを削除
        (resSource as maplibregl.GeoJSONSource).setData({
            type: 'FeatureCollection',
            features: []
        });
    }
    
    if(!linePolygonFeatures.length) { return; }

    // line, polygonのハイライトを削除
    linePolygonFeatures.forEach((feature) => {
        const sourceLayer = feature.layer.id;
        if (sourceLayer.includes(LAYER_GEOMETRY_TYPES.polygonOutline)) {
            resetLine(map, sourceLayer, KEY);
        } else if (sourceLayer.includes(LAYER_GEOMETRY_TYPES.polygon)) {
            resetFill(map, sourceLayer, KEY);
        }else if (sourceLayer.includes(LAYER_GEOMETRY_TYPES.line)) {
            resetLine(map, sourceLayer, KEY);
        }

    });
};
